import {
  type BookedFlightItinerary,
  type AirBookingFlightItinerarySlice,
  type BookedFlightItineraryWithDepartureTime,
  type FlightItineraryResponse,
  TravelItineraryEnum,
} from "@b2bportal/air-booking-api";
import {
  type CacheableExternalResource,
  CallState,
  type ExternalResource,
} from "@b2bportal/core-types";
import { createSelector } from "@reduxjs/toolkit";
import type { FlightState } from "../../../store";
import { ALL_FLIGHT_STATES, flightsPostBookingApi } from "./api";

// Hoisted at module level so the RTK Query selector instance is stable
const _selectAllItinerariesQuery =
  flightsPostBookingApi.endpoints.fetchAllFlightItineraries.select({
    states: ALL_FLIGHT_STATES,
    referenceDateTime: "",
  });

//  TODO show changed itinerary times if a minor schedule change exists
export const getDepartureSlice = (
  bookedItinerary: BookedFlightItinerary
): AirBookingFlightItinerarySlice => {
  switch (bookedItinerary.travelItinerary.TravelItinerary) {
    case TravelItineraryEnum.SingleTravelItinerary:
      return bookedItinerary.travelItinerary.slices[0];
    case TravelItineraryEnum.MultiTravelItinerary:
      return bookedItinerary.travelItinerary.travelItineraries[0].slices[0];
  }
};

export const getReturnSlice = (
  bookedItinerary: BookedFlightItinerary
): AirBookingFlightItinerarySlice | undefined => {
  switch (bookedItinerary.travelItinerary.TravelItinerary) {
    case TravelItineraryEnum.SingleTravelItinerary: {
      const slices = bookedItinerary.travelItinerary.slices;
      return slices.length > 1 ? slices[1] : undefined;
    }
    case TravelItineraryEnum.MultiTravelItinerary: {
      const itineraries = bookedItinerary.travelItinerary.travelItineraries;
      return itineraries.length > 1 ? itineraries[1].slices[0] : undefined;
    }
  }
};

export const getAirports = (state: FlightState) =>
  state.flights.flightPostBooking.context.airports;
export const getAirlines = (state: FlightState) =>
  state.flights.flightPostBooking.context.airlines;

type FlightItinerariesData = FlightItineraryResponse["itineraries"];

export const getFlightItineraries = createSelector(
  _selectAllItinerariesQuery,
  (
    result
  ): CacheableExternalResource<FlightItinerariesData, { error: string }> => {
    if (result.isUninitialized) {
      return { state: CallState.NotCalled };
    }
    if (result.isSuccess) {
      return { state: CallState.Success, data: result.data.itineraries };
    }
    if (result.isLoading) {
      return { state: CallState.InProcess, data: result.data?.itineraries };
    }
    if (result.isError) {
      return {
        state: CallState.Failed,
        data: result.data?.itineraries,
        error: result.error as { error: string },
      };
    }
    return { state: CallState.NotCalled };
  }
);

const _getSelectedItineraryId = (state: FlightState) =>
  state.flights.flightPostBooking.selectedItineraryId;

// RTK Query memoizes per (itineraryId) — using the result as a createSelector
// input gives a stable reference so the output object is only recreated when
// the query data actually changes.
const _getSelectedItineraryQueryResult = createSelector(
  _getSelectedItineraryId,
  (state: FlightState) => state,
  (id, state) => {
    if (id == null) return null;
    return flightsPostBookingApi.endpoints.fetchFlightItinerary.select({
      itineraryId: id,
      referenceDateTime: "",
    })(state);
  }
);

export const getSelectedFlightItinerary = createSelector(
  _getSelectedItineraryId,
  _getSelectedItineraryQueryResult,
  (
    id,
    result
  ): ExternalResource<
    BookedFlightItineraryWithDepartureTime,
    { error: string }
  > => {
    if (id == null || result == null) {
      return { state: CallState.NotCalled };
    }
    if (result.isUninitialized) {
      return { state: CallState.NotCalled };
    }
    if (result.isSuccess) {
      return { state: CallState.Success, data: result.data.itinerary };
    }
    if (result.isLoading) {
      return { state: CallState.InProcess };
    }
    if (result.isError) {
      return {
        state: CallState.Failed,
        error: result.error as { error: string },
      };
    }
    return { state: CallState.NotCalled };
  }
);
